import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Redirect, Prompt } from 'react-router-dom';
import useUserStore from '../stores/useUserStore';
import useScheduleStore, {
  computeEffectiveAssignments,
  BlockBar,
} from '../stores/useScheduleStore';
import { useThemeStore } from '../stores/useThemeStore';
import ScheduleHeader from './ScheduleHeader';
import ScheduleGrid from './ScheduleGrid';
import OOOModal from './OOOModal';
import LogoSpinner from '../components/ui/LogoSpinner';
import CellAssignmentPopover from './CellAssignmentPopover';
import DragController from './DragController';
import { useScheduleDerived } from './useScheduleDerived';
import { OOOEntry } from './types';
import Reveal from '../components/ui/Reveal';
import { DOCK_MOTION } from '../components/ChatBot/flerpieDock';

const SCHEDULE_GRID_PREFS_KEY = 'scheduleGridPrefs';
const loadGridPrefs = (): { showUnavailableBlocks: boolean } => {
  try {
    const raw = localStorage.getItem(SCHEDULE_GRID_PREFS_KEY);
    if (!raw) return { showUnavailableBlocks: true };
    const parsed = JSON.parse(raw);
    return {
      showUnavailableBlocks:
        typeof parsed?.showUnavailableBlocks === 'boolean' ? parsed.showUnavailableBlocks : true,
    };
  } catch {
    return { showUnavailableBlocks: true };
  }
};
const EMPTY_BLOCK_BAR_MAP: Map<string, Map<string, BlockBar>> = new Map();

const StaffSchedulePage: React.FC = () => {
  const user = useUserStore(s => s.user);
  const dayMode = useThemeStore(s => s.dayMode);
  // Per-field selectors: Zustand re-renders only when the selected slice
  // changes (referential equality). Avoids a whole-store subscription that
  // would tick on every drag move (~60Hz). The action methods are stable
  // store-level refs and don't cause re-renders.
  const days = useScheduleStore(s => s.days);
  const rangeStart = useScheduleStore(s => s.rangeStart);
  const rangeEnd = useScheduleStore(s => s.rangeEnd);
  const events = useScheduleStore(s => s.events);
  const staff = useScheduleStore(s => s.staff);
  const ooo = useScheduleStore(s => s.ooo);
  const pendingChanges = useScheduleStore(s => s.pendingChanges);
  const pendingBlockChanges = useScheduleStore(s => s.pendingBlockChanges);
  const loading = useScheduleStore(s => s.loading);
  const saving = useScheduleStore(s => s.saving);
  const error = useScheduleStore(s => s.error);
  const saveSummary = useScheduleStore(s => s.saveSummary);
  const fetchRange = useScheduleStore(s => s.fetchRange);
  const saveChanges = useScheduleStore(s => s.saveChanges);
  const clearPendingChanges = useScheduleStore(s => s.clearPendingChanges);
  const assignments = useScheduleStore(s => s.assignments);
  const shifts = useScheduleStore(s => s.shifts);
  const unavailableBlocks = useScheduleStore(s => s.unavailableBlocks);

  // Event-grain merged assignments — used by the popover's "Assigned" list to
  // know identity (which events this user is on, regardless of which days).
  const effectiveAssignments = useMemo(
    () => computeEffectiveAssignments(assignments, pendingChanges),
    [assignments, pendingChanges]
  );

  // Everything the grid derives from store state. Shared with the crew view
  // (CrewScheduleTimeline) so the two cannot drift — see useScheduleDerived.
  const {
    eventColorMap, eventLookup, eventsPerDay, oooByUserDay,
    effectiveDayBars, effectiveBlockBars, hasEmptyDays,
  } = useScheduleDerived();

  const isDirty = pendingChanges.length > 0 || pendingBlockChanges.length > 0;

  // Collapse empty days state
  const [collapsedEmpty, setCollapsedEmpty] = useState(false);

  // Persisted: scheduler's choice to show/hide unavailability block bars
  const [gridPrefs, setGridPrefs] = useState(() => loadGridPrefs());
  useEffect(() => {
    try {
      localStorage.setItem(SCHEDULE_GRID_PREFS_KEY, JSON.stringify(gridPrefs));
    } catch {
      // localStorage quota — silent ignore
    }
  }, [gridPrefs]);
  const toggleShowBlocks = useCallback(() => {
    setGridPrefs(p => ({ ...p, showUnavailableBlocks: !p.showUnavailableBlocks }));
  }, []);

  // OOO modal state (for editing OOO from cell click)
  const [oooModalOpen, setOOOModalOpen] = useState(false);
  const [editingOOO, setEditingOOO] = useState<OOOEntry | null>(null);

  // Popover state
  const [popover, setPopover] = useState<{
    open: boolean;
    userId: string;
    dateStr: string;
    eventId?: string;
    position: { top: number; left: number };
  }>({ open: false, userId: '', dateStr: '', position: { top: 0, left: 0 } });

  // Capability check
  const canAccess = user.can_admin || user.can_producer;

  // Fetch data on mount. rangeStart/rangeEnd come from the store's default
  // window; nothing refetches on scroll, because the whole window is already here.
  useEffect(() => {
    if (canAccess) fetchRange(rangeStart, rangeEnd);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  // Bumped by the header's Today button. The grid watches it and scrolls; the
  // page owns it because the button and the scroll container are in different
  // subtrees. A counter rather than a boolean so repeat presses re-fire.
  const [jumpToken, setJumpToken] = useState(0);
  const jumpToToday = useCallback(() => setJumpToken(t => t + 1), []);

  // Beforeunload warning for unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Escape key to close popover/modal
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (oooModalOpen) { setOOOModalOpen(false); setEditingOOO(null); }
        else if (popover.open) setPopover(p => ({ ...p, open: false }));
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [oooModalOpen, popover.open]);

  // Cell click handler — receives rect from ScheduleCell (no window.event!)
  const handleCellClick = useCallback((userId: string, dateStr: string, rect: DOMRect, eventId?: string) => {
    if (eventId === '__ooo__') {
      const userOOO = oooByUserDay.get(userId)?.get(dateStr);
      if (userOOO && userOOO.length > 0) {
        setEditingOOO(userOOO[0]);
        setOOOModalOpen(true);
      }
      return;
    }

    const pos = {
      top: Math.min(rect.bottom + 4, window.innerHeight - 300),
      left: Math.min(rect.left, window.innerWidth - 300),
    };

    setPopover({ open: true, userId, dateStr, eventId, position: pos });
  }, [oooByUserDay]);

  const handleEditOOO = useCallback((entry: OOOEntry) => {
    setEditingOOO(entry);
    setOOOModalOpen(true);
  }, []);

  if (!canAccess) return <Redirect to="/" />;

  return (
    <div className={`min-h-page p-4 ${dayMode ? 'day-mode bg-gray-50 text-gray-900' : ''}`}>
      <Prompt
        when={isDirty}
        message="You have unsaved schedule changes. Discard and leave?"
      />
      <ScheduleHeader
        collapsedEmpty={collapsedEmpty}
        onToggleCollapse={() => setCollapsedEmpty(prev => !prev)}
        hasEmptyDays={hasEmptyDays}
        showUnavailableBlocks={gridPrefs.showUnavailableBlocks}
        onToggleShowBlocks={toggleShowBlocks}
        onJumpToToday={jumpToToday}
      />

      {loading && (
        <LogoSpinner label="Loading the schedule" />
      )}

      <Reveal show={!!error}>
        <div className="bg-red-500/10 day:bg-red-50 border border-red-500/30 day:border-red-300 rounded-lg px-4 py-3 mb-4 text-sm text-red-400 day:text-red-700">
          {error}
        </div>
      </Reveal>

      {/* Partial-save outcome — amber, NOT red: most of the save succeeded and
          the still-pending items remain in the save bar below for retry. */}
      <Reveal show={!!saveSummary}>
        <div className="bg-amber-500/10 day:bg-amber-50 border border-amber-500/30 day:border-amber-300 rounded-lg px-4 py-3 mb-4 text-sm text-amber-400 day:text-amber-700">
          {saveSummary}
        </div>
      </Reveal>

      {/* INVARIANT: the grid must stay mounted across a save. The post-save
          refetch is `quiet` (no loading flip) specifically so this subtree is
          NOT torn down — that preserves the horizontal scroll/anchor. Do not
          add a `key` to <ScheduleGrid> or move it behind a save-time flag, or
          the "jumps to start of month" bug returns. */}
      {!loading && (
        <>
          <ScheduleGrid
            onCellClick={handleCellClick}
            collapsedEmpty={collapsedEmpty}
            eventColorMap={eventColorMap}
            eventLookup={eventLookup}
            eventsPerDay={eventsPerDay}
            oooByUserDay={oooByUserDay}
            effectiveDayBars={effectiveDayBars}
            effectiveBlockBars={gridPrefs.showUnavailableBlocks ? effectiveBlockBars : EMPTY_BLOCK_BAR_MAP}
            jumpToken={jumpToken}
          />
          <DragController eventLookup={eventLookup} shifts={shifts} />
        </>
      )}

      {/* Save bar */}
      {isDirty && (
        <div className={`fixed bottom-0 left-[var(--flerpie-dock,0px)] right-0 transition-[left] ${DOCK_MOTION} z-30 bg-gray-850 day:bg-white border-t border-gray-700 day:border-gray-200
          px-6 py-3 flex items-center justify-between shadow-lg day:shadow-gray-900/10 animate-slideUp`}>
          <div className="text-sm text-on-surface day:text-gray-700">
            {pendingChanges.length > 0 && (
              <>
                <span className="text-primary day:text-amber-700 font-semibold">{pendingChanges.length}</span>
                {' '}shift change{pendingChanges.length !== 1 ? 's' : ''}
              </>
            )}
            {pendingChanges.length > 0 && pendingBlockChanges.length > 0 && (
              <span className="mx-1 text-on-surface-variant day:text-gray-500">·</span>
            )}
            {pendingBlockChanges.length > 0 && (
              <>
                <span className="text-slate-300 day:text-slate-700 font-semibold">{pendingBlockChanges.length}</span>
                {' '}unavailability change{pendingBlockChanges.length !== 1 ? 's' : ''}
              </>
            )}
            {pendingChanges.slice(0, 3).map(pc => (
              <span key={pc.id} className="ml-2 text-xs text-on-surface-variant day:text-gray-500">
                {pc.action === 'add' ? '+' : '-'}{pc.user_name?.split(' ')[0]} &rarr; {pc.event_name}
              </span>
            ))}
            {pendingChanges.length > 3 && (
              <span className="ml-1 text-xs text-on-surface-variant day:text-gray-500">+{pendingChanges.length - 3} more</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={clearPendingChanges}
              className={buttonClass('quiet', 'md')}>
              Discard
            </button>
            <button onClick={() => saveChanges()} disabled={saving}
              className={buttonClass('filled', 'md')}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* OOO Modal */}
      <OOOModal
        isOpen={oooModalOpen}
        onClose={() => { setOOOModalOpen(false); setEditingOOO(null); }}
        editEntry={editingOOO}
        staff={staff}
        isProducer={true}
      />

      {/* Popover — keyed on (user, date) so the internal markingBlock/notes
          state resets when the user opens a different cell. */}
      {popover.open && (
        <CellAssignmentPopover
          key={`${popover.userId}-${popover.dateStr}`}
          isOpen={popover.open}
          onClose={() => setPopover(p => ({ ...p, open: false }))}
          userId={popover.userId}
          userName={staff.find(s => s.id === popover.userId)
            ? `${staff.find(s => s.id === popover.userId)!.firstname} ${staff.find(s => s.id === popover.userId)!.lastname}`
            : ''}
          dateStr={popover.dateStr}
          eventId={popover.eventId}
          eventsOnDay={eventsPerDay.get(popover.dateStr) || []}
          userAssignments={(effectiveDayBars.get(popover.userId)?.get(popover.dateStr) || [])
            .filter(b => !b.isPendingRemove)
            .map(b => ({ event_id: b.event_id, role: b.role }))}
          eventColorMap={eventColorMap}
          oooEntries={oooByUserDay.get(popover.userId)?.get(popover.dateStr) || []}
          blockBar={effectiveBlockBars.get(popover.userId)?.get(popover.dateStr) || null}
          onEditOOO={handleEditOOO}
          position={popover.position}
        />
      )}
    </div>
  );
};

export default StaffSchedulePage;
